/**
 * Feedback Service
 * Submits in-room feedback (feature request / bug report) to support@sevv.com
 * Includes automatic technical context for developer triage.
 */

import Parse from 'parse'
import { getServerBaseUrl } from '../utils/serverUrl'
import store from '../stores'
import { getConsoleErrorBuffer } from '../utils/consoleErrorBuffer'

export interface FeedbackPayload {
  type: 'bug' | 'feature' | 'rating'
  message?: string
  starRating?: number
  wouldRecommend?: 'yes' | 'maybe' | 'no'
}

export interface FeedbackResult {
  success: boolean
  error?: string
}

export interface FeedbackContext {
  url?: string
  pathname?: string
  viewport?: string
  screen?: string
  environment?: string
  language?: string
  timezone?: string
  online?: boolean
  connectionStatus?: string
  consoleErrors?: string
  timeInRoomSeconds?: number
}

function collectFeedbackContext(type: 'bug' | 'feature' | 'rating'): FeedbackContext {
  const state = store.getState()

  let url: string | undefined
  let pathname: string | undefined
  try {
    url = window.location.href
    pathname = window.location.pathname
    if (url && url.includes('?')) {
      url = url.split('?')[0]
    }
  } catch {
    /* ignore */
  }

  let viewport: string | undefined
  let screen: string | undefined
  try {
    viewport = `${window.innerWidth} x ${window.innerHeight}`
    const dpr = window.devicePixelRatio ?? 1
    screen = `${window.screen.width} x ${window.screen.height} (${dpr}x)`
  } catch {
    /* ignore */
  }

  let environment: string | undefined
  try {
    const isDev = !!(import.meta as any).env?.DEV
    const host = window.location.hostname || 'unknown'
    environment = `${isDev ? 'development' : 'production'} (${host})`
  } catch {
    environment = 'unknown'
  }

  let language: string | undefined
  let timezone: string | undefined
  try {
    language = navigator.language || undefined
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  } catch {
    /* ignore */
  }

  let online: boolean | undefined
  try {
    online = navigator.onLine
  } catch {
    /* ignore */
  }

  const connectionStatus = state.user?.connectionStatus ?? undefined

  const ctx: FeedbackContext = {
    url,
    pathname,
    viewport,
    screen,
    environment,
    language,
    timezone,
    online,
    connectionStatus,
  }

  if (type === 'bug') {
    ctx.consoleErrors = getConsoleErrorBuffer()
  }

  const roomJoinedAt = sessionStorage.getItem('feedback_roomJoinedAt')
  if (roomJoinedAt) {
    const joined = parseInt(roomJoinedAt, 10)
    if (!isNaN(joined)) {
      ctx.timeInRoomSeconds = Math.floor((Date.now() - joined) / 1000)
    }
  }

  return ctx
}

export async function submitFeedback(payload: FeedbackPayload): Promise<FeedbackResult> {
  const { type } = payload
  const baseUrl = getServerBaseUrl()
  const url = `${baseUrl}/api/email/feedback`

  const state = store.getState()
  const roomId = state.room?.roomId ?? null
  const roomName = state.room?.roomName ?? ''

  const sessionToken = Parse.User.current()?.getSessionToken?.() ?? null

  const context = collectFeedbackContext(type)

  const userData = state.user?.userData
  const parseUser = Parse.User.current()
  const clientUserName = userData
    ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || userData.username || userData.email || undefined
    : (parseUser?.get('username') || parseUser?.get('email')) as string | undefined
  const clientUserEmail = userData?.email || (parseUser?.get('email') as string | undefined)
  const clientUserId = userData?.id || parseUser?.id

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (sessionToken) {
    headers['Authorization'] = `Bearer ${sessionToken}`
  }

  const body: Record<string, unknown> = {
    type,
    ...(roomId && { roomId }),
    ...(roomName && { roomName }),
    context,
  }
  if (clientUserName) body.userName = clientUserName
  if (clientUserEmail) body.userEmail = clientUserEmail
  if (clientUserId) body.userId = clientUserId

  if (type === 'rating') {
    body.starRating = payload.starRating
    body.wouldRecommend = payload.wouldRecommend
    body.message = (payload.message || '').trim()
  } else {
    body.message = (payload.message || '').trim()
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    })

    const data = await response.json().catch(() => ({}))

    if (!response.ok) {
      return {
        success: false,
        error: data.error || data.message || `Request failed (${response.status})`,
      }
    }

    if (!data.success) {
      return {
        success: false,
        error: data.error || 'Failed to send feedback',
      }
    }

    return { success: true }
  } catch (err: any) {
    return {
      success: false,
      error: err?.message || 'Network error. Please try again.',
    }
  }
}
